import React, { useCallback, useRef, useEffect, useMemo, useState } from "react";
import { MarkerF, PolylineF } from "@react-google-maps/api";
import { Box } from "@mui/material";
import BaseMapContainer from "./BaseMapContainer";
import TechnicianMarker from "./TechnicianMarker";
import ETACard from "../ETACard";
import { TrackingPosition, TrackingSession } from "../../../../shared/tracking/trackingSlice";
import { dtGreen } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { haversineDistanceKm } from "../../../../shared/utils/geoUtils";

// ===================================================================
//  TRACKING STATUS CODES (must match backend enum)
// ===================================================================

export const TrackingStatus = {
  Active: 1,
  Paused: 2,
  Arrived: 3,
  Ended: 4,
} as const;

// ===================================================================
//  POLYLINE DASH ANIMATION HOOK
// ===================================================================

const useDashOffset = (isNearby: boolean): number => {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (isNearby) return;

    // Update every 150ms for smooth-looking dash flow without excessive re-renders
    const startTime = performance.now();
    const interval = setInterval(() => {
      const elapsed = performance.now() - startTime;
      setOffset((elapsed / 1000) * 20);
    }, 150);

    return () => clearInterval(interval);
  }, [isNearby]);

  return offset;
};

// ===================================================================
//  COMPONENT
// ===================================================================

interface TrackingMapContainerProps {
  session: TrackingSession | null;
  position: TrackingPosition | null;
  prevPosition?: TrackingPosition | null;
  isStationary?: boolean;
  isArrivalCelebrating?: boolean;
}

const TrackingMapContainer: React.FC<TrackingMapContainerProps> = ({
  session,
  position,
  prevPosition,
  isStationary = false,
  isArrivalCelebrating = false,
}) => {
  const { translate } = useTranslation(["serviceTracking"]);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [routeDissolving, setRouteDissolving] = useState(false);

  const technicianLat = position?.latitude ?? session?.lastLat ?? null;
  const technicianLng = position?.longitude ?? session?.lastLng ?? null;

  const destinationLat = session?.destinationLat ?? null;
  const destinationLng = session?.destinationLng ?? null;

  // Previous position for heading calculation
  const prevLat = prevPosition?.latitude ?? null;
  const prevLng = prevPosition?.longitude ?? null;

  // Distance to destination
  const distanceKm = useMemo(() => {
    if (
      technicianLat != null &&
      technicianLng != null &&
      destinationLat != null &&
      destinationLng != null
    ) {
      return haversineDistanceKm(technicianLat, technicianLng, destinationLat, destinationLng);
    }
    return null;
  }, [technicianLat, technicianLng, destinationLat, destinationLng]);

  const isNearby = distanceKm != null && distanceKm < 5;

  // Flowing dash offset
  const dashOffset = useDashOffset(isNearby);

  // Compute initials from provider name
  const initials = useMemo(() => {
    const name = session?.providerName || "";
    const parts = name.split(" ").filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase() || "??";
  }, [session?.providerName]);

  // Center map on technician position
  const center = useMemo(() => {
    if (technicianLat != null && technicianLng != null) {
      return { lat: technicianLat, lng: technicianLng };
    }
    if (destinationLat != null && destinationLng != null) {
      return { lat: destinationLat, lng: destinationLng };
    }
    return { lat: 49.8, lng: 15.5 }; // Default CZ/SK center
  }, [technicianLat, technicianLng, destinationLat, destinationLng]);

  // Auto-pan to technician when position updates
  useEffect(() => {
    if (
      mapRef.current &&
      technicianLat != null &&
      technicianLng != null &&
      !isArrivalCelebrating
    ) {
      const currentCenter = mapRef.current.getCenter();
      if (currentCenter) {
        const latDiff = Math.abs(currentCenter.lat() - technicianLat);
        const lngDiff = Math.abs(currentCenter.lng() - technicianLng);
        // Only pan if the marker has moved significantly from center of view
        if (latDiff > 0.01 || lngDiff > 0.01) {
          mapRef.current.panTo({ lat: technicianLat, lng: technicianLng });
        }
      }
    }
  }, [technicianLat, technicianLng, isArrivalCelebrating]);

  // Arrival celebration: auto-zoom to destination
  useEffect(() => {
    if (isArrivalCelebrating && mapRef.current && destinationLat != null && destinationLng != null) {
      mapRef.current.panTo({ lat: destinationLat, lng: destinationLng });
      mapRef.current.setZoom(16);
      // Start route dissolve
      setRouteDissolving(true);
    } else {
      setRouteDissolving(false);
    }
  }, [isArrivalCelebrating, destinationLat, destinationLng]);

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const isArrived = session?.status === TrackingStatus.Arrived;
  const etaMinutes = position?.etaMinutes ?? session?.initialEtaMinutes ?? null;

  // Route polyline path
  const routePath = useMemo(() => {
    if (
      technicianLat != null &&
      technicianLng != null &&
      destinationLat != null &&
      destinationLng != null
    ) {
      return [
        { lat: technicianLat, lng: technicianLng },
        { lat: destinationLat, lng: destinationLng },
      ];
    }
    return null;
  }, [technicianLat, technicianLng, destinationLat, destinationLng]);

  // Polyline options: green dashed, solid+thicker when <5km
  const polylineOptions = useMemo((): google.maps.PolylineOptions => {
    if (routeDissolving) {
      return {
        strokeColor: "#10B981",
        strokeOpacity: 0.15,
        strokeWeight: 2,
        geodesic: true,
      };
    }
    if (isNearby) {
      return {
        strokeColor: "#10B981",
        strokeOpacity: 0.85,
        strokeWeight: 6,
        geodesic: true,
      };
    }
    return {
      strokeColor: "#10B981",
      strokeOpacity: 0.7,
      strokeWeight: 4,
      geodesic: true,
      icons: [
        {
          icon: {
            path: "M 0,-1 0,1",
            strokeOpacity: 1,
            scale: 4,
          },
          offset: `${dashOffset % 100}%`,
          repeat: "20px",
        },
      ],
    };
  }, [isNearby, routeDissolving, dashOffset]);

  // Don't show route if arrived and dissolving is done
  const showRoute = routePath != null && !isArrived;

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      <BaseMapContainer
        center={center}
        zoom={13}
        onLoad={handleMapLoad}
        loadingText={translate("serviceTracking:loadingMap")}
      >
        {/* Route polyline */}
        {showRoute && routePath && (
          <PolylineF
            path={routePath}
            options={polylineOptions}
          />
        )}

        {/* Technician marker */}
        {technicianLat != null && technicianLng != null && (
          <TechnicianMarker
            lat={technicianLat}
            lng={technicianLng}
            heading={position?.heading ?? null}
            prevLat={prevLat}
            prevLng={prevLng}
            initials={initials}
            isStationary={isStationary}
          />
        )}

        {/* Destination marker (red pin) */}
        {destinationLat != null && destinationLng != null && (
          <MarkerF
            position={{ lat: destinationLat, lng: destinationLng }}
            icon={{
              url: "data:image/svg+xml," + encodeURIComponent(
                `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
                  <path d="M16 0C7.16 0 0 7.16 0 16c0 12 16 24 16 24s16-12 16-24C32 7.16 24.84 0 16 0z" fill="#EF4444"/>
                  <circle cx="16" cy="16" r="7" fill="#FFFFFF"/>
                  <circle cx="16" cy="16" r="3" fill="#EF4444"/>
                </svg>`
              ),
              scaledSize: new google.maps.Size(32, 40),
              anchor: new google.maps.Point(16, 40),
            }}
          />
        )}
      </BaseMapContainer>

      {/* ETA Card overlay */}
      <ETACard
        etaMinutes={etaMinutes}
        isArrived={isArrived}
      />
    </Box>
  );
};

export default React.memo(TrackingMapContainer);
